/**
 * Decides when to read a progress update aloud while Tally works out an answer.
 *
 * A question costs p50 ~10s and p90 ~36s of model time. On screen a spinner
 * covers that; in a voice session it is dead air, which reads as "it broke".
 * The assistant tree already emits progress updates ("Looking up the numbers")
 * as it moves between assistants, so the gap can be narrated with text we
 * already have.
 *
 * The rule is: never cut Tally off mid-phrase, and when it is free, say the
 * update that is on screen now. Steps that came and went while it was talking
 * are skipped rather than queued, so the voice never falls behind the screen.
 * Whether Tally is free is the caller's to know; this module only decides what
 * is worth saying.
 */

/**
 * Wait this long before narrating anything. The acknowledgement plays first,
 * and a question that comes back quickly should just be answered.
 */
export const NARRATION_INITIAL_DELAY_MS = 1_500;

/**
 * Quiet this long - since the turn began or since anything was last said - and
 * a stock filler phrase is played, so a long step never reads as a dead line.
 */
export const FILLER_AFTER_MS = 5_000;

export interface NarrationState {
  /** When the current thinking turn began. */
  startedAt: number;
  /** The last progress update actually spoken, if any. */
  lastSpoken: string | null;
  /** When it was spoken. */
  lastSpokenAt: number | null;
  /** The progress update currently on screen. */
  observedText: string;
  /** When a filler phrase was last played, if any. */
  lastFilledAt: number | null;
}

export function newNarrationState(now: number): NarrationState {
  return {
    startedAt: now,
    lastSpoken: null,
    lastSpokenAt: null,
    observedText: "",
    lastFilledAt: null,
  };
}

/** Records what the screen currently says. */
export function observeProgress(
  state: NarrationState,
  text: string,
): NarrationState {
  if (text === state.observedText) return state;
  return { ...state, observedText: text };
}

/** True when the update on screen is new and the turn is past its first beat. */
export function shouldNarrate(state: NarrationState, now: number): boolean {
  const text = state.observedText;
  if (text.trim() === "") return false;

  // The tree re-sends the same update while an assistant is still working.
  // Saying it twice sounds like a stutter.
  if (text === state.lastSpoken) return false;

  return now - state.startedAt >= NARRATION_INITIAL_DELAY_MS;
}

/** Marks the currently observed update as spoken. */
export function recordNarration(
  state: NarrationState,
  now: number,
): NarrationState {
  return { ...state, lastSpoken: state.observedText, lastSpokenAt: now };
}

/**
 * True when nothing has been said for FILLER_AFTER_MS. The turn's start counts
 * as speech: an acknowledgement is played then.
 */
export function shouldFill(state: NarrationState, now: number): boolean {
  const lastSound = Math.max(
    state.startedAt,
    state.lastSpokenAt ?? 0,
    state.lastFilledAt ?? 0,
  );
  return now - lastSound >= FILLER_AFTER_MS;
}

/** Marks a filler as played. */
export function recordFill(state: NarrationState, now: number): NarrationState {
  return { ...state, lastFilledAt: now };
}
